'use client';

import { Icon } from '@/components/ui/Icon';
import clsx from 'clsx';
import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react';
import styles from './Select.module.scss';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Label text for the select */
  label?: ReactNode;
  /** Helper text displayed below the select */
  helperText?: string;
  /** Error message to display */
  error?: string;
  /** Whether the select has an error */
  hasError?: boolean;
  /** Color variant: primary (green) for light backgrounds, secondary (white) for dark backgrounds */
  variant?: 'primary' | 'secondary';
  /** Whether the select should take full width */
  fullWidth?: boolean;
  /** Options for the select */
  options: SelectOption[];
  /** Placeholder text */
  placeholder?: string;
}

/**
 * Select component with bottom-border design, chevron, label, and error handling.
 * Automatically generates accessible IDs for label-select association.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      helperText,
      error,
      hasError,
      variant = 'primary',
      fullWidth = false,
      options,
      placeholder,
      className,
      id: providedId,
      disabled,
      required,
      value,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;

    const showError = hasError || !!error;
    const describedBy =
      [showError ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') ||
      undefined;

    const resolvedPlaceholder = placeholder && required ? `${placeholder}*` : placeholder;
    const isControlled = value !== undefined;
    const normalizedValue = value ?? '';
    const hasMatchingOption = options.some((option) => option.value === normalizedValue);

    let placeholderOption: ReactNode = null;
    if (resolvedPlaceholder) {
      placeholderOption = (
        <option value="" disabled>
          {resolvedPlaceholder}
        </option>
      );
    } else if (isControlled && !hasMatchingOption) {
      placeholderOption = <option value="" hidden />;
    }

    return (
      <div
        className={clsx(
          styles.wrapper,
          {
            [styles.fullWidth]: fullWidth,
            [styles.hasError]: showError,
            [styles.disabled]: disabled,
            [styles.secondary]: variant === 'secondary',
          },
          className
        )}
      >
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
            {required && (
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        <div className={styles.selectWrapper}>
          <select
            ref={ref}
            id={id}
            disabled={disabled}
            required={required}
            aria-required={required || undefined}
            aria-invalid={showError}
            aria-describedby={describedBy}
            className={clsx(styles.select, {
              [styles.isPlaceholder]: isControlled && !normalizedValue,
            })}
            {...(isControlled ? { value: normalizedValue } : {})}
            {...props}
          >
            {placeholderOption}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className={styles.arrow} aria-hidden="true">
            <Icon name="chevron-down" size={18} />
          </span>
        </div>

        {showError && error && (
          <span id={errorId} className={styles.error} role="alert">
            {error}
          </span>
        )}

        {helperText && !showError && (
          <span id={helperId} className={styles.helperText}>
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
